/**
 * Push-notification registration (QUEUED — wire in on the next native build).
 *
 * Wired into AuthContext (registerPush on login, unregisterPush on logout) but
 * INERT until the FCM library is installed: loadMessaging() loads
 * `@react-native-firebase/messaging` via an eval-indirected require so neither
 * Metro (bundle time) nor tsc tries to resolve it before it is installed — until
 * then every call here is a safe no-op that returns null.
 *
 * Next-build wiring (see docs/push-notifications-setup.md):
 *   - install @react-native-firebase/app + @react-native-firebase/messaging
 *   - add the native config (entitlements / Info.plist / google-services.json)
 *   - call registerPush() after a user logs in; unregisterPush() on logout
 */
import { Platform } from 'react-native';
import { apiPost, apiDelete } from '../api/client';

/** Best-effort load of the FCM messaging module; null when not installed. */
function loadMessaging(): any | null {
  try {
    const pkg = ['@react-native-firebase', 'messaging'].join('/');
    // Resolve `require` INDIRECTLY (via eval) so Metro's bundler never tries to
    // statically include the not-yet-installed firebase module. A bare `import(pkg)`
    // (computed specifier) is a hard error in release bundling, and a static
    // `require('@react-native-firebase/messaging')` would fail module resolution at
    // bundle time. This indirection keeps the file safe to bundle today; until the
    // lib is installed in the next native build, the require throws → caught → null
    // (also the no-op path on Hermes, which disables eval).
    // eslint-disable-next-line no-eval
    const req: ((m: string) => any) | null = (() => { try { return (0, eval)('require'); } catch { return null; } })();
    const mod: any = req ? req(pkg) : null;
    return mod ? (mod.default ?? mod) : null;
  } catch {
    return null; // lib not installed yet — no-op
  }
}

/**
 * Ask for notification permission, fetch the device token, and register it with
 * the backend. Safe to call repeatedly (token registration is an upsert).
 * Returns the token on success, or null when push is unavailable/declined.
 */
export async function registerPush(): Promise<string | null> {
  if (Platform.OS === 'web') return null;
  const messaging = await loadMessaging();
  if (!messaging) return null;

  try {
    const authStatus = await messaging().requestPermission();
    // 1 = AUTHORIZED, 2 = PROVISIONAL in @react-native-firebase/messaging.
    const granted = authStatus === 1 || authStatus === 2;
    if (!granted) return null;

    const token: string = await messaging().getToken();
    if (!token) return null;

    await apiPost('/api/me/device-tokens', {
      platform: Platform.OS === 'ios' ? 'ios' : 'android',
      token,
    });

    // Keep the backend current if the OS rotates the token.
    messaging().onTokenRefresh(async (next: string) => {
      try {
        await apiPost('/api/me/device-tokens', {
          platform: Platform.OS === 'ios' ? 'ios' : 'android',
          token: next,
        });
      } catch { /* best-effort */ }
    });

    return token;
  } catch {
    return null;
  }
}

/** Deactivate the current device token server-side (call on logout). */
export async function unregisterPush(): Promise<void> {
  if (Platform.OS === 'web') return;
  const messaging = await loadMessaging();
  if (!messaging) return;
  try {
    const token: string = await messaging().getToken();
    if (token) await apiDelete(`/api/me/device-tokens/${encodeURIComponent(token)}`);
  } catch { /* best-effort */ }
}
